import { setHtml } from '../utils/dom-set.js';
/**
 * Global "unsaved changes" tracker.
 *
 * - Call `markDirty()` whenever a change is made (forms call this on input).
 * - Call `markClean()` after a successful save.
 * - Use `confirmIfDirty(action)` to wrap navigation actions; if dirty, the
 *   user gets a Save / Discard / Cancel prompt.
 * - `attachAutoDirty(container)` wires every input/textarea/select inside the
 *   container so a change anywhere flips the global dirty flag.
 * - The banner's "Save" button auto-finds the visible #btnSave* control on
 *   the active tab. No page-level registration needed.
 * - `setupBeforeUnload()` is called once at app bootstrap and warns on tab close.
 */

let dirty = false;
let listeners = [];
// Optional per-view save action. A tab/form can register the exact thing the
// banner's "Save" button should do for what's currently on screen — used where
// the right control can't be found by scanning for a visible `#btnSave…`
// (e.g. the Confidential tab, whose active save differs per sub-view).
// `saverOwner` is an element that lives only while that view is mounted; once
// it's detached the saver is treated as stale and ignored.
let activeSaver = null;
let saverOwner = null;
// The user explicitly dismissed the banner with ×. Only that should be able to
// hide it while work is still unsaved — see the reconciler below.
let userDismissed = false;
let reconcileTimer = null;
// The active view autosaves through its registered saver; banner stays hidden
// while that is going well (see registerSaver).
let autosaveManaged = false;
let autosaveHealthy = true;
// Guard so multiple navigation events firing in quick succession only ever
// surface a single confirm dialog. Without this, clicking a tab while
// another tab-switch was already prompting would stack a second overlay
// behind the first; each new click adds another invisible backdrop on
// top of the previous one, darkening the screen on each press.
let pendingConfirm = null;

export function isDirty() { return dirty; }

let lastDirtyEl = null; // the control the user most recently edited

// Diagnostic only (zero behaviour change): a markDirty with NO user
// interaction in the last second is the "phantom unsaved changes" bug —
// log who called it so the source can be pinned from a live console.
let _lastUserEventAt = 0;
if (typeof window !== 'undefined') {
  ['pointerdown', 'keydown'].forEach(t =>
    window.addEventListener(t, () => { _lastUserEventAt = Date.now(); }, { capture: true, passive: true }));
  // Trusted `input` events count as user activity too: dictation, drag-drop
  // text, and Playwright's fill() all edit fields WITHOUT a preceding
  // keydown/pointerdown — without this they'd starve the stamp and the
  // phantom guard below would swallow their legitimate dirty flags.
  // Programmatic dispatchEvent(new Event('input')) is isTrusted=false and
  // rich editors' rt-change is synthetic, so phantom sources stay excluded.
  window.addEventListener('input', (e) => { if (e.isTrusted) _lastUserEventAt = Date.now(); }, { capture: true, passive: true });
}

/** When the user last pressed a key or pointer — consumers (live refresh)
 *  use it to avoid re-rendering under an actively-interacting user. */
export function lastUserInputAt() { return _lastUserEventAt; }

export function markDirty(el, opts = {}) {
  // PHANTOM-DIRTY GUARD. Every real edit is preceded by a trusted pointer/key
  // event within moments; a markDirty arriving with NO recent user input is a
  // programmatic echo — a rich editor's setHtml on tab mount, a prefill, a
  // restore — and flagging those threw the "Unsaved changes" dialog in front
  // of staff who had just SAVED (and in front of CI, meln-proof et al,
  // 14 Sep 2026). Those calls are now dropped, not just logged. A flow that
  // legitimately dirties the page long after the click that started it (the
  // AI rewrite applying its result) passes { force: true }.
  if (!opts.force && Date.now() - _lastUserEventAt > 1500) {
    console.warn('[EASY] markDirty suppressed - no recent user input (phantom):', new Error('trace').stack?.split('\n').slice(2, 5).join(' | '));
    return;
  }
  // Remember WHERE the edit happened so the banner's "Go to save" can aim at
  // the save button belonging to THAT form, not just the first one on the
  // page — the Summary tab alone has three visible save buttons.
  if (el && el.nodeType === 1) lastDirtyEl = el;
  // ALWAYS re-show the banner, even when we were already dirty. Dismissing it
  // with the × only hides it (dirty stays true), so the old `if (dirty) return`
  // meant that after one dismiss the banner never came back no matter how much
  // more you typed — you could edit a whole tab with no visible way to save and
  // lose the lot on navigation. Only the listeners are edge-triggered.
  const wasDirty = dirty;
  dirty = true;
  userDismissed = false; // a fresh edit always re-earns the banner
  const _b = document.getElementById('dirtyBanner');
  if (_b?.classList.contains('dirty-banner-saved')) {
    _b.classList.remove('dirty-banner-saved');
    const t = _b.querySelector('.dirty-banner-text');
    if (t) t.textContent = 'You have unsaved changes';
  }
  showBanner();
  startReconciler();
  if (!wasDirty) listeners.forEach(fn => { try { fn(true); } catch {} });
}

/**
 * While work is unsaved, the save affordance MUST stay on screen.
 *
 * Staff reported the banner "flashing up then hiding again", leaving no way to
 * save and no sign anything was wrong. Any re-render, tab bind, or stray
 * markClean() could take it away — and a banner that vanishes is worse than no
 * banner, because it tells the user their work is safe when it isn't. Rather
 * than chase every caller, this reconciles the truth once a second: if we are
 * dirty and the user has not explicitly dismissed it, the banner is visible.
 */
function startReconciler() {
  if (reconcileTimer) return;
  reconcileTimer = setInterval(() => {
    if (!dirty) { clearInterval(reconcileTimer); reconcileTimer = null; return; }
    if (userDismissed) return;
    if (dialogOwnsSave() || (autosaveManaged && autosaveHealthy && saverOwner?.isConnected)) {
      // Step aside while a self-saving dialog is up, or while a healthy
      // autosave view owns the page; re-assert the moment that stops being true.
      const el = document.getElementById('dirtyBanner');
      if (el) el.classList.remove('visible');
      return;
    }
    const el = document.getElementById('dirtyBanner');
    if (!el || !el.classList.contains('visible')) showBanner();
  }, 1000);
}

export function markClean() {
  if (!dirty) return;
  dirty = false;
  userDismissed = false;
  autosaveHealthy = true; // a landed save clears any earlier autosave failure
  if (reconcileTimer) { clearInterval(reconcileTimer); reconcileTimer = null; }
  // If the banner is on screen when the save lands (meetings, the postcode
  // editor, any autosaving surface), don't just yank it — a vanishing banner
  // right as you reach for "Go to save" reads as a glitch. It flips to a
  // "✓ Saved" receipt and STAYS until the user dismisses it, edits again
  // (markDirty restores the normal banner), or navigates away.
  const el = document.getElementById('dirtyBanner');
  document.body.classList.remove('dirty-active');
  if (el?.classList.contains('visible') && !el.classList.contains('dirty-banner-failed')) {
    const txt = el.querySelector('.dirty-banner-text');
    if (txt) txt.textContent = '✓ Saved';
    el.classList.add('dirty-banner-saved');
    if (!window._dtSavedNavBound) {
      window._dtSavedNavBound = true;
      window.addEventListener('hashchange', () => {
        const b = document.getElementById('dirtyBanner');
        if (b?.classList.contains('dirty-banner-saved')) {
          b.classList.remove('dirty-banner-saved');
          const t = b.querySelector('.dirty-banner-text');
          if (t) t.textContent = 'You have unsaved changes';
          if (!dirty) hideBanner();
        }
      });
    }
  } else {
    hideBanner();
  }
  listeners.forEach(fn => { try { fn(false); } catch {} });
}


export function onDirtyChange(fn) { listeners.push(fn); }

/**
 * Register the save action the bottom banner's "Save" button should run for
 * the current view. Pass a function (may be async) that performs the save and
 * calls markClean() on success. Returns an unregister fn. Registering again
 * replaces the previous saver, so the most-recently-mounted view wins.
 */
export function registerSaver(fn, ownerEl = null, opts = {}) {
  activeSaver = typeof fn === 'function' ? fn : null;
  saverOwner = ownerEl;
  // autosave:true — this view saves ITSELF (debounced autosave through the
  // same saver). While that is true and healthy, the banner stays out of the
  // way entirely: flashing "unsaved changes" for the two seconds before an
  // autosave lands is exactly the glitchy feeling staff describe, and it
  // teaches them to ignore the banner. It comes back — red — the moment an
  // autosave FAILS, which is the only time it has something true to say.
  autosaveManaged = opts.autosave === true;
  autosaveHealthy = true;
  return () => { if (activeSaver === fn) { activeSaver = null; saverOwner = null; autosaveManaged = false; } };
}

/** An autosave failed — the banner has something true to say again. */
export function reportAutosaveFailure() {
  console.error('[EASY] autosave failure reported — surfacing the save banner');
  autosaveHealthy = false;
  if (dirty) showBanner();
}
export function clearSaver() { activeSaver = null; saverOwner = null; }

/**
 * Run the currently-registered saver, if its view is still mounted.
 *
 * This is the SAME path the banner's Save button takes, exposed so a view can
 * autosave without depending on the user noticing a banner. Returns true if a
 * save actually ran. Never throws — the saver surfaces its own error UI, and an
 * autosave must never break the thing the user is typing into.
 */
export async function runActiveSaver() {
  if (typeof activeSaver !== 'function') return false;
  if (saverOwner && !saverOwner.isConnected) { activeSaver = null; saverOwner = null; return false; }
  try { await activeSaver(); return true; } catch { return false; }
}

/**
 * Wrap an action that would discard unsaved changes (tab switch, navigate away,
 * close panel). If dirty, prompt the user.
 *
 * Returns a Promise<boolean>: true means "proceed with the action".
 */
export function confirmIfDirty(actionLabel = 'continue') {
  if (!dirty) return Promise.resolve(true);
  // If a confirm is already on screen, return its promise instead of
  // mounting a second overlay. Whichever button the user clicks resolves
  // every queued caller with the same answer.
  if (pendingConfirm) return pendingConfirm;

  pendingConfirm = new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'confirm-overlay';
    // Built via DOM API (no innerHTML) so actionLabel — though always
    // a hardcoded developer string in practice — can't ever flow into
    // the page as markup.
    const dialog = document.createElement('div');
    dialog.className = 'confirm-dialog confirm-dialog-narrow';
    const h3 = document.createElement('h3');
    h3.textContent = 'Unsaved changes';
    const p = document.createElement('p');
    p.className = 'confirm-dialog-hint';
    p.textContent = `You have unsaved changes. Save them before you ${actionLabel}?`;
    const actions = document.createElement('div');
    actions.className = 'confirm-actions confirm-actions-wrap';
    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn btn-outline';
    cancelBtn.id = 'dirtyCancel';
    cancelBtn.textContent = 'Cancel';
    const discardBtn = document.createElement('button');
    discardBtn.className = 'btn btn-outline u-text-danger';
    discardBtn.id = 'dirtyDiscard';
    discardBtn.textContent = 'Discard changes';
    const saveBtn = document.createElement('button');
    saveBtn.className = 'btn btn-primary';
    saveBtn.id = 'dirtySave';
    // Whether this dialog can ACTUALLY save decides what the primary button is.
    // The old button said "Save & Continue" and saved nothing — markClean() +
    // proceed, i.e. Discard wearing a Save label. That is the 28 Aug 2026
    // "staff did hours of work, pressed save, it was gone" incident. Same rule
    // as the banner now: never claim a save you cannot perform.
    const canReallySave = typeof activeSaver === 'function' && (!saverOwner || saverOwner.isConnected);
    saveBtn.textContent = canReallySave ? 'Save & continue' : 'Go back & save';
    actions.append(cancelBtn, discardBtn, saveBtn);
    dialog.append(h3, p, actions);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    const close = () => { overlay.remove(); pendingConfirm = null; };
    overlay.querySelector('#dirtyCancel').addEventListener('click', () => { close(); resolve(false); });
    overlay.querySelector('#dirtyDiscard').addEventListener('click', () => { markClean(); close(); resolve(true); });
    overlay.querySelector('#dirtySave').addEventListener('click', async () => {
      // Re-check at click time — the saver's view may have unmounted while
      // the dialog sat open.
      const stillSavable = canReallySave && typeof activeSaver === 'function' && (!saverOwner || saverOwner.isConnected);
      if (stillSavable) {
        saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
        let threw = false;
        try { await activeSaver(); } catch { threw = true; }
        // A successful save calls markClean() — possibly a beat after the
        // saver's promise settles. Wait for it before trusting the result.
        if (!threw) {
          const deadline = Date.now() + 15000;
          while (dirty && Date.now() < deadline) await new Promise(r => setTimeout(r, 250));
        }
        if (!threw && !dirty) { close(); resolve(true); return; }
        // The save FAILED. Do not proceed, do not discard — stay on the page
        // with the work intact and say so loudly.
        close(); resolve(false); reportSaveFailed(); return;
      }
      // No saver registered for this view: stay on the page and point the user
      // at the section's real Save button instead of pretending.
      close(); resolve(false); guideToSaveControl();
    });
    overlay.addEventListener('click', (e) => { if (e.target === overlay) { close(); resolve(false); } });
  });
  return pendingConfirm;
}

/**
 * Auto-wire every form control under `container` so user input flips the dirty flag.
 * Skips elements with `data-no-dirty` attribute (e.g. search boxes).
 */
export function attachAutoDirty(container) {
  if (!container) return;
  // Guard against re-binding: renderDetail() calls this on EVERY render, and
  // the container element persists across renders (only its innerHTML is
  // swapped). Without this, each render stacked another full set of
  // listeners on the same node.
  if (container.dataset && container.dataset.autoDirtyBound === '1') return;
  if (container.dataset) container.dataset.autoDirtyBound = '1';
  const handler = (e) => {
    // Only flip the dirty flag for events that came from the user. Programmatic
    // dispatchEvent(new Event('input')) fires with isTrusted=false — used in
    // several places (postcode hidden input, multiselect sync, rich-text
    // editors mounting) to keep the DOM in sync without simulating user input.
    // Without this guard those internal events would fire the unsaved-changes
    // banner the moment a student detail panel rendered, blocking subsequent
    // dialogs (PDF download / tab switch / etc.) behind a spurious confirm.
    if (!e.isTrusted) return;
    const t = e.target;
    if (!t || !t.matches) return;
    // Include contenteditable so typing directly into a rich-text / editable
    // region reliably flags dirty even if its widget forgets to dispatch a
    // synthetic rt-change event. This was the main cause of the
    // "save prompt doesn't always appear" inconsistency.
    if (!t.matches('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return;
    // File inputs are uploads — they persist immediately (Storage + Firestore),
    // so selecting a file is never an "unsaved change" to the surrounding form.
    // Without this, every doc/photo upload trips the confusing "save changes"
    // banner even though there's nothing to save.
    if (t.matches('input[type="file"]')) return;
    if (t.closest('[data-no-dirty]')) return;
    if (t.hasAttribute('data-no-dirty')) return;
    // Ignore search-style inputs
    if (t.id && /search|filter/i.test(t.id)) return;
    markDirty(t);
  };
  container.addEventListener('input', handler, true);
  container.addEventListener('change', handler, true);
  // Custom widgets (multi-select, rich-text) dispatch their own change
  // events on real user edits. These are synthetic (isTrusted=false) so the
  // handler above skips them — listen for them explicitly so selecting an
  // ESO / diagnosis or editing rich text still flips the dirty flag.
  const customHandler = (e) => {
    const t = e.target;
    if (t && (t.closest?.('[data-no-dirty]') || (t.id && /search|filter/i.test(t.id)))) return;
    markDirty(t && t.nodeType === 1 ? t : undefined);
  };
  container.addEventListener('ms-change', customHandler, true);
  container.addEventListener('rt-change', customHandler, true);
}

// === Bottom banner ===
function ensureBanner() {
  let el = document.getElementById('dirtyBanner');
  if (el) return el;
  el = document.createElement('div');
  el.id = 'dirtyBanner';
  el.className = 'dirty-banner';
  setHtml(el, `<span class="dirty-banner-icon">●</span>
    <span class="dirty-banner-text">You have unsaved changes</span>
    <button class="btn btn-outline btn-sm u-text-danger" id="dirtyBannerDiscard">Discard</button>
    <button class="btn btn-primary btn-sm" id="dirtyBannerSave">Go to save →</button>
    <button class="dirty-banner-close" id="dirtyBannerClose" title="Dismiss banner (changes still unsaved)" aria-label="Dismiss">&times;</button>`);
  document.body.appendChild(el);
  el.querySelector('#dirtyBannerClose').addEventListener('click', () => {
    // Hide the banner without affecting dirty state. The next dirty event
    // (or tab switch / navigation guard) will surface the prompt again.
    // This is the ONLY thing allowed to keep it hidden while work is unsaved —
    // the reconciler respects this flag and nothing else.
    userDismissed = true;
    hideBanner();
  });
  el.querySelector('#dirtyBannerDiscard').addEventListener('click', async () => {
    // Discarding = mark clean + reload the page so the form reverts to its
    // last saved state. The previous version did literally nothing.
    if (confirm('Discard unsaved changes? This will reload the page.')) {
      markClean();
      window.location.reload();
    }
  });
  el.querySelector('#dirtyBannerSave').addEventListener('click', async (e) => {
    const sb = e.currentTarget;
    if (sb.dataset.saving === '1') return; // no double-fire while a save is in flight
    // THE BANNER IS A GUIDE, NOT A SAVER. Every data-entry surface carries its
    // own real Save button; the banner's job is to take the user to it —
    // scroll it into view, pulse it, and say which button to press. The old
    // design ran a registered saver from here (and before that, proxy-clicked
    // whatever button it could name-match), and that bred a week of races,
    // self-clicks and false NOT SAVED alarms. Predictable beats magic.
    const guided = await guideToSaveControl();
    if (guided) {
      // The pulse on the real button is the guide now; the banner steps aside
      // and only a fresh edit brings it back.
      userDismissed = true;
      hideBanner();
      return;
    }
    // No real button on this surface — the triangle-style autosave views have
    // none, and the banner only shows there when autosave is FAILING. Running
    // the registered saver is the retry the user is asking for. Only while its
    // owner element is still in the DOM: otherwise it's a stale saver from a
    // view we've navigated away from.
    if (typeof activeSaver === 'function' && (!saverOwner || saverOwner.isConnected)) {
      sb.dataset.saving = '1'; sb.disabled = true; const L = sb.textContent; sb.textContent = 'Saving…';
      let threw = false;
      try { await activeSaver(); } catch { threw = true; }
      finally { sb.disabled = false; sb.textContent = L; delete sb.dataset.saving; }
      // A successful save calls markClean() — but not necessarily before the
      // saver's promise settles (some delegate). Give the write a fair chance
      // to land before shouting.
      if (!threw) {
        const deadline = Date.now() + 15000;
        while (dirty && Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 250));
        }
      }
      if (threw || dirty) reportSaveFailed();
      return;
    }
    // NOTHING could be saved and nowhere to guide to. Never fail silently on
    // an explicit press: say so and leave the dirty flag protecting the work.
    reportSaveFailed();
  });
  return el;
}

/**
 * Find the visible save control that belongs to the user's current edit,
 * scroll to it and pulse it. Shared by the banner's "Go to save" and the
 * navigation dialog's "Go back & save". Returns true if a control was found.
 *
 * Candidate = a VISIBLE, ENABLED save control: any id containing "save",
 * plus the explicit [data-save-btn] opt-in (five of ~25 save buttons are
 * literally named btnSave*; assessSave, bratingSave, icSaveNote … are not).
 * Never a hidden one — clicking a hidden button once wrote an empty plan
 * over a user's note. Never the banner's own button, whose id also
 * matches (the self-click no-op caught by save-failure-surfaces.spec.js).
 */
async function guideToSaveControl() {
  const findCandidates = () => Array.from(document.querySelectorAll('button[id*="ave"], button[data-save-btn]'))
    .filter(b => /save/i.test(b.id) || b.hasAttribute('data-save-btn'))
    .filter(b => !b.closest('#dirtyBanner'))
    .filter(b => b.offsetParent !== null && !b.disabled);
  let candidates = findCandidates();
  if (!candidates.length) {
    // Savers and their buttons mount ASYNCHRONOUSLY (dynamic import + a
    // Firestore read). Give the view a moment before judging it empty.
    await new Promise(r => setTimeout(r, 800));
    candidates = findCandidates();
  }
  if (!candidates.length) return false;
  // Several surfaces show more than one save button at once (Summary:
  // Allied health, Attendance, Library) — pulsing the wrong one tells the
  // user to press a button that won't save their work. Prefer the first
  // candidate AFTER the last-edited control in document order (forms put
  // Save below the fields), then the nearest before it, then the first.
  let target = candidates[0];
  if (lastDirtyEl?.isConnected) {
    const after = candidates.find(c =>
      lastDirtyEl.compareDocumentPosition(c) & 4 /* DOCUMENT_POSITION_FOLLOWING */);
    const before = [...candidates].reverse().find(c =>
      lastDirtyEl.compareDocumentPosition(c) & 2 /* DOCUMENT_POSITION_PRECEDING */);
    target = after || before || target;
  }
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target.classList.add('save-pulse');
  setTimeout(() => target.classList.remove('save-pulse'), 3500);
  import('../components/toast.js').then(m => m.toast(`Press ${target.textContent.trim() || 'Save'} to keep your changes`)).catch(() => {});
  return true;
}

/**
 * An explicit Save did not save. Say so unmistakably — a toast is too easy to
 * miss for something this serious — and leave the dirty flag set so the
 * navigation guard still protects the work.
 */
function reportSaveFailed() {
  const el = document.getElementById('dirtyBanner');
  if (el) {
    el.classList.add('visible', 'dirty-banner-failed');
    const txt = el.querySelector('.dirty-banner-text');
    if (txt) txt.textContent = 'NOT SAVED — your changes are still unsaved. Copy anything you cannot lose, then try the Save button inside the section.';
  }
  import('../components/toast.js')
    .then(m => m.toast('Your changes were NOT saved. Nothing has been written — please copy anything you cannot lose.'))
    .catch(() => {});
  console.error('[EASY] Save requested from the banner but nothing was saved.');
}

/**
 * A modal that carries its OWN save controls (Add & Send / Save Without
 * Sending / Save all / Done…) owns the save story while it is open. Showing
 * the global banner underneath it was worse than useless: its Save cannot
 * drive a modal flow, so it escalated to a red NOT SAVED while Liz's note was
 * saving fine through the dialog's own buttons — and the reconciler kept
 * resurrecting it every second. Dirty state is still tracked; the banner
 * returns the moment the dialog closes.
 */
function dialogOwnsSave() {
  return !!document.querySelector('.confirm-overlay, .modal-overlay, .sbe-overlay, .mtss-modal-overlay');
}

function showBanner() {
  if (dialogOwnsSave()) { hideBanner(); return; }
  if (autosaveManaged && autosaveHealthy && saverOwner?.isConnected) { hideBanner(); return; }
  // QUIET ZONES. A surface whose own save control is unmistakable and right
  // there (the staff-note composer: Add / Add & Notify sits directly under
  // the editor) can declare data-quiet-save on its container — the banner
  // stays out of the way instead of floating OVER the very button it would
  // point at. Dirty tracking itself is untouched: the tab/navigation guard
  // still confirms before a typed draft can be lost.
  if (lastDirtyEl?.isConnected && lastDirtyEl.closest('[data-quiet-save]')) { hideBanner(); return; }
  const el = ensureBanner();
  // Clear any previous "NOT SAVED" state — a fresh edit means we're back to the
  // ordinary unsaved-changes prompt, not still reporting the old failure.
  el.classList.remove('dirty-banner-failed');
  const txt = el.querySelector('.dirty-banner-text');
  if (txt && txt.textContent !== 'You have unsaved changes') txt.textContent = 'You have unsaved changes';
  el.classList.add('visible');
  document.body.classList.add('dirty-active');
}
function hideBanner() {
  const el = document.getElementById('dirtyBanner');
  if (el) el.classList.remove('visible');
  document.body.classList.remove('dirty-active');
}

// === Browser close warning ===
let beforeUnloadAttached = false;
export function setupBeforeUnload() {
  if (beforeUnloadAttached) return;
  beforeUnloadAttached = true;
  window.addEventListener('beforeunload', (e) => {
    if (!dirty) return;
    e.preventDefault();
    e.returnValue = '';
    return '';
  });
}

// Exposed for the stale-chunk recovery handler in main.js, which must be
// able to check dirtiness even when module imports are failing.
if (typeof window !== 'undefined') window.nccdIsDirty = isDirty;
